import {Injectable} from '@angular/core';
import {Ast, Token, TokenRule} from '../../../types';
import {ExpressionTokenizerService} from './expression-tokenizer.service';
import {ExpressionParserService} from './expression-parser.service';

/**
 * Expression statement compiler
 */
@Injectable()
export class ExpressionCompilerService {
  /**
   * Injects dependencies
   */
  public constructor(
    private tokenizer: ExpressionTokenizerService,
    private parser: ExpressionParserService
  ) {
  }

  /**
   * Sets custom token rules
   *
   * @param {TokenRule[]} tokenRules
   */
  public setTokenRules(tokenRules: TokenRule[]) {
    this.tokenizer.setTokenRules(tokenRules);
  }

  /**
   * Returns currently set token rules
   *
   * @returns: TokenRules[]
   */
  public getTokenRules(): TokenRule[] {
    return this.tokenizer.getTokenRules();
  }

  /**
   * Tokenizes given expression statement
   *
   * @param {string} stmt
   * @param {TokenRule[]} rules
   * @returns {Token[]};
   */
  public tokenize(stmt: string, rules: TokenRule[] = null): Token[] {
    return this.tokenizer.tokenize(stmt, rules ? rules : this.tokenizer.getTokenRules());
  }

  /**
   * Parses given expression statement into ast
   *
   * @param {string} stmt
   * @param {TokenRule[]} rules
   * @returns {Ast.Group}
   */
  public parse(stmt: string, rules: TokenRule[] = null): Ast.Group {
    return this.parser.parse(stmt, rules);
  }

  /**
   * Compiles given ast into expression statement
   *
   * @param {Ast.Group} ast
   * @param {{target: string}} opts
   * @returns {string}
   */
  public compile(ast: Ast.Group, opts?: {target: 'expression' | 'js'}) {
    return this.compileGroup(ast, opts);
  }

  /**
   * Cache for toJs() results
   */
  private jsCache: {[stmt: string]: string} = {};

  /**
   * Parses expression statement and compiles it to executable javascript.
   * Results are cached so each expression is only parsed once.
   *
   * @param {string} stmt
   * @returns {string}
   */
  public toJs(stmt: string): string {
    if (!this.jsCache[stmt]) {
      const ast = this.parse(stmt);
      this.jsCache[stmt] = this.compile(ast, {target: 'js'});
    }
    return this.jsCache[stmt];
  }

  /**
   * Compiles ast group into expression statement
   *
   * @param {Ast.Group} group
   * @param {{target: string}} opts
   * @returns {string}
   */
  private compileGroup(group: Ast.Group, opts?: {target: 'expression' | 'js'}) {
    const compiler = (buf: string, element: Ast.Group | Ast.Expression) => {
      buf && (buf += ` ${group.operator} `);
      buf += element.type === Ast.TYPE.GROUP ? this.compileGroup(<Ast.Group>element, opts) : this.compileExpression(<Ast.Expression>element, opts);
      return buf;
    };

    return `(${group.elements.reduce(compiler, '')})`;
  }

  /**
   * Compiles ast expression into expression statement
   *
   * @param {Ast.Expression} expr
   * @param {{target: string}} opts
   * @returns {string}
   */
  private compileExpression(expr: Ast.Expression, opts?: {target: 'expression' | 'js'}) {
    const target = opts && opts.target || 'expression';

    if (expr.operator === '~' && target === 'js') {
      return `(/${expr.value}/).test(${expr.variable})`;
    }

    let value = expr.value;

    if (typeof expr.value === 'string') {
      value = `"${expr.value}"`;
    }

    if (typeof expr.value === 'object' && expr.value !== null && typeof (<Date>expr.value).getMonth === 'function') {
      value = `"${(<Date>expr.value).toJSON()}"`;
    }

    return `${expr.variable} ${expr.operator} ${value}`;
  }
}
